"use client"

import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react"

const testimonials = [
  {
    name: "Anna Kowalska",
    company: "Sklep internetowy",
    content:
      "Współpraca z Panią Elżbietą to czysta przyjemność. Zawsze mogę liczyć na profesjonalną obsługę i szybkie odpowiedzi na moje pytania. Polecam każdemu przedsiębiorcy!",
    rating: 5,
  },
  {
    name: "Marek Nowicki",
    company: "Usługi budowlane",
    content:
      "Od kilku lat korzystam z usług tego biura i nigdy nie miałem żadnych problemów. Wszystkie rozliczenia są robione terminowo, a Pani Elżbieta zawsze doradzi najlepsze rozwiązanie.",
    rating: 5,
  },
  {
    name: "Katarzyna Wiśniewska",
    company: "Salon kosmetyczny",
    content:
      "Polecam z całego serca! Dzięki pomocy Pani Elżbiety bezproblemowo założyłam działalność i od początku mam wszystko pod kontrolą. Profesjonalizm na najwyższym poziomie.",
    rating: 5,
  },
  {
    name: "Tomasz Lewandowski",
    company: "Transport",
    content:
      "Bardzo dobry kontakt, szybka realizacja zleceń i co najważniejsze - żadnych stresu przed terminami. Biuro godne polecenia dla każdego przedsiębiorcy.",
    rating: 5,
  },
  {
    name: "Magdalena Zielińska",
    company: "Gabinet stomatologiczny",
    content:
      "Profesjonalne podejście i pełne zaangażowanie w sprawy klienta. Pani Elżbieta pomogła mi zoptymalizować koszty i wybrać najlepszą formę opodatkowania.",
    rating: 5,
  },
  {
    name: "Piotr Kamiński",
    company: "IT Freelancer",
    content:
      "Jako freelancer potrzebowałem kogoś, kto zrozumie specyfikę mojej pracy. Biuro Pani Elżbiety spełniło wszystkie moje oczekiwania. Super współpraca!",
    rating: 5,
  },
]

export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const itemsPerPage = 3

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev + itemsPerPage >= testimonials.length ? 0 : prev + itemsPerPage
    )
  }

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? Math.max(0, testimonials.length - itemsPerPage) : prev - itemsPerPage
    )
  }

  const visibleTestimonials = testimonials.slice(
    currentIndex,
    currentIndex + itemsPerPage
  )

  return (
    <section id="opinie" className="py-20 md:py-32 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              Opinie klientów
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mt-4 text-balance">
              Co mówią o nas nasi klienci
            </h2>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={prevSlide}
              className="border-border hover:bg-secondary hover:border-accent"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="sr-only">Poprzednie opinie</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={nextSlide}
              className="border-border hover:bg-secondary hover:border-accent"
            >
              <ChevronRight className="h-5 w-5" />
              <span className="sr-only">Następne opinie</span>
            </Button>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleTestimonials.map((testimonial, index) => (
            <Card
              key={currentIndex + index}
              className="bg-card border-border hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-6">
                <Quote className="w-10 h-10 text-accent/30 mb-4" />
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-accent text-accent"
                    />
                  ))}
                </div>
                <p className="text-foreground leading-relaxed mb-6">
                  &ldquo;{testimonial.content}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-primary-foreground font-semibold text-sm">
                      {testimonial.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">
                      {testimonial.name}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: Math.ceil(testimonials.length / itemsPerPage) }).map(
            (_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index * itemsPerPage)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  Math.floor(currentIndex / itemsPerPage) === index
                    ? "bg-accent"
                    : "bg-border hover:bg-muted-foreground/50"
                }`}
                aria-label={`Przejdź do strony ${index + 1}`}
              />
            )
          )}
        </div>
      </div>
    </section>
  )
}
